import type { ApolloError, ServerError, ServerParseError } from '@apollo/client';
import type { GraphQLError, GraphQLErrorExtensions, GraphQLFormattedError } from 'graphql';
import { getErrorStatusCode } from './error.utils';

/**
 * All the shapes the Apollo error link can hand us as an "exception":
 * - `GraphQLError` (server-side error class)
 * - `GraphQLFormattedError` (serialized version reaching the client)
 * - `ServerError` / `ServerParseError` (raw HTTP transport failures)
 * - Plain `Error` (typically `TypeError: Failed to fetch`, Auth0 refresh errors…)
 */
export type ApolloErrorLike = GraphQLError | GraphQLFormattedError | ServerError | ServerParseError | Error;

const hasExtensions = (error: unknown): error is { extensions?: { code?: string } } => {
  if (!error || typeof error !== 'object') return false;
  return 'extensions' in error;
};

const isGraphQLShape = (error: ApolloErrorLike): error is GraphQLError =>
  hasExtensions(error) && (error as { extensions?: unknown }).extensions !== undefined;

const isGraphQLFormattedShape = (error: ApolloErrorLike): error is GraphQLFormattedError =>
  !!error && typeof error === 'object' && 'path' in error;

const isServerErrorShape = (error: ApolloErrorLike): error is ServerError =>
  !!error && typeof error === 'object' && 'result' in error;

/**
 * Returns `true` when the error was raised because a fetch/GraphQL request was
 * intentionally aborted (component unmount, route change, refetch race,
 * `AbortController` timeout…). These are almost always benign — the caller
 * cancelled the request on purpose — so we don't want to surface them as
 * Apollo/Sentry incidents (cf. TEC-1684, Sentry FORM-BLOOMERS-QW3).
 *
 * Matches the standard `name` set by the Fetch spec on the `DOMException`
 * raised when an associated `AbortSignal` fires, which is the shape Apollo's
 * HTTP link (and `apollo-upload-client`) surfaces as `networkError`.
 */
export const isAbortError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  return (error as { name?: unknown }).name === 'AbortError';
};

/**
 * Extracts the business-level error code (`extensions.code`) from any Apollo
 * error shape. Returns `undefined` when the error has no such code (typical of
 * pure transport failures).
 */
export const extractApolloErrorCode = (error: ApolloErrorLike): string | undefined => {
  if (isGraphQLShape(error) || isGraphQLFormattedShape(error)) {
    return (error.extensions?.code as string | undefined) ?? undefined;
  }
  if (isServerErrorShape(error)) {
    const result = error.result as { errors?: Array<{ extensions?: { code?: string } }> } | undefined;
    return result?.errors?.[0]?.extensions?.code;
  }
  return undefined;
};

/**
 * Extracts the business-level error code from an `ApolloError` wrapper by
 * looking first at the transport-level `networkError` (typically a
 * `ServerError` carrying a JSON body) and then at the first `GraphQLError`
 * returned by the server. Thin convenience wrapper around
 * `extractApolloErrorCode`.
 */
export const getApolloErrorCode = (apolloErr: ApolloError | undefined): string | undefined => {
  if (!apolloErr) return undefined;
  if (apolloErr.networkError) {
    const codeFromNetwork = extractApolloErrorCode(apolloErr.networkError);
    if (codeFromNetwork) return codeFromNetwork;
  }
  const firstGraphQLError = apolloErr.graphQLErrors?.[0];
  if (firstGraphQLError) {
    return extractApolloErrorCode(firstGraphQLError);
  }
  return undefined;
};

/**
 * Returns the HTTP status code carried by an `ApolloError` wrapper, looking
 * first at the `networkError` then falling back to the first `graphQLErrors`.
 * Defaults to `500` when no status is reachable (cf. `getErrorStatusCode`).
 */
export const getApolloErrorStatusCode = (apolloErr: ApolloError): number => {
  if (apolloErr.networkError) {
    return getErrorStatusCode(apolloErr.networkError);
  }
  if (apolloErr.graphQLErrors) {
    return getErrorStatusCode(apolloErr.graphQLErrors[0]);
  }
  return 500;
};

/**
 * Returns the raw `extensions` object attached to the first error carried by
 * an `ApolloError`, checking `networkError.result.errors[0].extensions` first
 * (typical HTTP transport failure with a JSON body) and falling back to
 * `graphQLErrors[0].extensions`. Returns `undefined` when neither carries an
 * extensions payload.
 */
export const getApolloErrorExtensions = (error: ApolloError): GraphQLErrorExtensions | undefined => {
  const networkErrorResult = (
    error.networkError as { result?: { errors?: Array<{ extensions?: GraphQLErrorExtensions }> } } | null
  )?.result;
  const firstNetworkExtensions = networkErrorResult?.errors?.[0]?.extensions;
  if (firstNetworkExtensions) {
    return firstNetworkExtensions;
  }
  return error.graphQLErrors?.[0]?.extensions;
};

/** Minimal signature of `react-i18next`'s `t` used by the Apollo error translator. */
export type ApolloErrorTranslateFn = (key: string, options?: Record<string, unknown>) => string;

/**
 * Translates an Apollo (or any) error to a human-readable French message.
 *
 * Reads the business code from `extensions.code` (see `BloomaysCustomError`)
 * and maps it against the `notify.errorCodes.*` table shipped by
 * `utils.shared/i18n/fr.json`. Falls back to the generic `notify.randomLKError`
 * message when no known code is found (TEC-3053) so the UI never surfaces the
 * raw technical English string.
 *
 * Callers only need to pass their own `t` function; the caller decides which
 * i18next instance / namespace loader wires the `notify` namespace.
 */
export const translateApolloError = (error: unknown, t: ApolloErrorTranslateFn): string => {
  const code = isApolloErrorLike(error) ? getApolloErrorCode(error) : undefined;
  if (code) {
    const translated = t(`errorCodes.${code}`, { ns: 'notify', defaultValue: '' });
    if (translated) return translated;
  }
  return t('randomLKError', { ns: 'notify' });
};

/**
 * Duck-types an unknown value as an `ApolloError` (has `graphQLErrors` /
 * `networkError`). We avoid importing the runtime `ApolloError` class here to
 * keep this module free of `@apollo/client` runtime dependencies — see the
 * `import type` at the top.
 */
const isApolloErrorLike = (error: unknown): error is ApolloError => {
  if (!error || typeof error !== 'object') return false;
  return 'graphQLErrors' in error || 'networkError' in error;
};
